import type { SiteOnTimeCounts, SiteOnTimeReport } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Field, Input } from '../../ui/form';
import { Panel } from '../../ui/layout';

// Дата по часам телефона, а не по UTC: в Казахстане до 05:00 UTC-дата ещё вчерашняя
const localIso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localIso(d);
};
const pct = (share: number | null) => (share === null ? '—' : `${Math.round(share * 1000) / 10} %`);
const shortDate = (date: string) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });

function Counts({ c }: { c: SiteOnTimeCounts }) {
  return (
    <span className="text-[13px] text-ink-2">
      вовремя {c.onTime} · поздно {c.late} · не сдано {c.missing} · всего {c.total}
    </span>
  );
}

/**
 * Доля вовремя сданных отчётов смены за период (ПМ-05): участко-дни по календарю графика, срок — час того дня.
 * Руководителю — по проектам, прорабу — свои участки.
 */
export function OnTimePanel({ projectId }: { projectId?: string }) {
  const me = useMe();
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));
  const q = useQuery({
    queryKey: ['site', me.tenant?.id, 'on-time', from, to, projectId],
    queryFn: () =>
      api<SiteOnTimeReport>('/site/on-time', { query: { from, to, ...(projectId ? { projectId } : {}) } }),
    enabled: Boolean(me.tenant?.staff) && Boolean(from) && Boolean(to),
  });
  const preset = (n: number) => {
    setFrom(daysAgo(n - 1));
    setTo(daysAgo(0));
  };
  const r = q.data;
  const reached = r?.share !== null && r?.share !== undefined && r.share >= r.target;
  return (
    <Panel title="Сдано вовремя за период">
      <div className="flex flex-wrap items-end gap-2">
        <Button variant="secondary" size="lg" onClick={() => preset(7)}>
          7 дней
        </Button>
        <Button variant="secondary" size="lg" onClick={() => preset(30)}>
          30 дней
        </Button>
        <Field label="С">
          <Input
            type="date"
            className="min-h-11"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
          />
        </Field>
        <Field label="По">
          <Input
            type="date"
            className="min-h-11"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
          />
        </Field>
      </div>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : !r || r.total === 0 ? (
        <EmptyState title="Нет участко-дней">
          За период нет рабочих дней с выданными участками, по которым срок уже прошёл.
        </EmptyState>
      ) : (
        <div className="mt-3 flex flex-col gap-3">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="text-[28px] font-semibold text-ink">{pct(r.share)}</span>
            <Badge tone={reached ? 'success' : 'warning'}>
              {reached ? 'Цель' : 'Ниже цели'} {pct(r.target)}
            </Badge>
            <Counts c={r} />
          </div>
          <p className="text-[12px] text-ink-2">
            Участко-день — рабочий день по календарю графика, на который выдан участок. Вовремя — отчёт
            отправлен на проверку до часа сдачи, действовавшего в тот день ({r.timezone}).
            {r.countedTo && r.countedTo < r.to
              ? ` Посчитано по ${shortDate(r.countedTo).replace(/\.$/, '')}.`
              : ''}
          </p>
          {r.projects.length > 1 ? (
            <ul className="divide-y divide-line">
              {r.projects.map((p) => (
                <li key={p.projectId} className="flex flex-col gap-1 py-2 sm:flex-row sm:justify-between">
                  <span className="text-[14px] font-medium text-ink">
                    {p.projectCode}: {p.projectName}
                  </span>
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="text-[14px] font-semibold">{pct(p.share)}</span>
                    <Counts c={p} />
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          <ul className="grid grid-cols-2 gap-1 sm:grid-cols-4">
            {r.days.map((d) => (
              <li
                key={d.date}
                className={`rounded-sm border px-2 py-1 text-[12px] ${
                  d.onTime === d.total ? 'border-line' : 'border-danger/40 bg-danger/5'
                }`}
              >
                <span className="font-medium">{shortDate(d.date)}</span> · до{' '}
                {String(d.hour).padStart(2, '0')}:00
                <br />
                {d.onTime} из {d.total} вовремя
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}
